// Dashboard_store_keeper.js - Elegant Bright Beautiful Version
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';

import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  LocalShipping as LocalShippingIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

import { styled, keyframes } from '@mui/material/styles';
import Sidebar from '../../../../components/sidebars/SideBarStoreKeep';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 24,
  transition: 'all 0.3s ease-in-out',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  border: '1px solid rgba(103, 58, 183, 0.1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(103, 58, 183, 0.15)',
  },
}));

const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(3),
  borderRadius: 28,
  background: `linear-gradient(135deg, ${color || '#667eea'} 0%, ${color || '#764ba2'} 100%)`,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeInUp} 0.6s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -30,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -30,
    left: -30,
    width: 100,
    height: 100,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
  },
}));

const GlowingIcon = styled(Avatar)(({ theme }) => ({
  background: 'rgba(255,255,255,0.2)',
  width: 64,
  height: 64,
  boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
  '& svg': {
    fontSize: 36,
  },
}));

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308'];

const DashboardStoreKeeper = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  
  const [dashboardData, setDashboardData] = useState({
    stats: {
      total_items: 0,
      low_stock_items: 0,
      out_of_stock: 0,
      total_value: 0,
      expiring_soon: 0,
      monthly_movement: [],
      category_distribution: [],
      top_moving_items: []
    },
    low_stock_items: [],
    expiring_items: [],
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const showNotification = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hotel/inventory/storekeeper_dashboard_data.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data);
      } else {
        showNotification(data.message || 'Failed to load dashboard data', 'error');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showNotification('Failed to connect to server', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const refreshData = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    showNotification('Dashboard refreshed successfully', 'success');
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const filteredLowStock = dashboardData.low_stock_items.filter(item =>
    item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredExpiring = dashboardData.expiring_items?.filter(item =>
    item.item_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CircularProgress size={80} thickness={4} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f0f4fc' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${sidebarOpen ? 260 : 0}px)` },
          minHeight: '100vh',
          overflow: 'auto',
          transition: 'width 0.3s ease',
          background: 'linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => {
            const alertCount = dashboardData.stats.low_stock_items + dashboardData.stats.expiring_soon;
            showNotification(`You have ${alertCount} items needing attention`, 'info');
          }}
          toggleSidebar={toggleSidebar}
        />

        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5, mt: 2 }}>
          <Box>
           
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
              Monitor inventory, track low stock, and manage expiring items
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={refreshData} 
              disabled={refreshing} 
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: '#f8f9ff', transform: 'rotate(180deg)', transition: 'transform 0.5s' }
              }}
            >
              <RefreshIcon sx={{ color: '#6366f1' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Stats Cards - Only 4 key metrics */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard elevation={0} color="#6366f1">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Total Items</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2.5rem', mt: 1 }}>{dashboardData.stats.total_items}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Active inventory items
                  </Typography>
                </Box>
                <GlowingIcon>
                  <StoreIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard elevation={0} color="#f97316">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Low Stock Alerts</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2.5rem', mt: 1 }}>{dashboardData.stats.low_stock_items}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Items below threshold
                  </Typography>
                </Box>
                <GlowingIcon>
                  <WarningIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard elevation={0} color="#ec4899">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Expiring Soon</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2.5rem', mt: 1 }}>{dashboardData.stats.expiring_soon}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Within 30 days
                  </Typography>
                </Box>
                <GlowingIcon>
                  <ScheduleIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
          
         
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} lg={7}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b' }}>
                    Stock Movement Trends
                  </Typography>
                  <Chip label="Last 6 Months" size="small" sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontWeight: 500 }} />
                </Box>
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={dashboardData.stats.monthly_movement}>
                    <defs>
                      <linearGradient id="colorReceived" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorIssued" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="received" stroke="#10b981" fill="url(#colorReceived)" name="Received" strokeWidth={3} />
                    <Area type="monotone" dataKey="issued" stroke="#f43f5e" fill="url(#colorIssued)" name="Issued" strokeWidth={3} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} lg={5}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b', mb: 3 }}>
                  Category Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={dashboardData.stats.category_distribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                      dataKey="item_count"
                    >
                      {dashboardData.stats.category_distribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Low Stock Items Section */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon sx={{ color: '#f97316' }} />
                      Low Stock Items
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                      Items that need immediate reordering
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ 
                      startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8' }} />,
                      sx: { borderRadius: 3, bgcolor: 'white' }
                    }}
                    sx={{ minWidth: 250 }}
                  />
                </Box>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#475569' }}>Current Stock</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#475569' }}>Threshold</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLowStock.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                            <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                            <Typography color="text.secondary">No low stock items found</Typography>
                            <Typography variant="caption" color="text.secondary">All inventory levels are healthy</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLowStock.map((item) => (
                          <TableRow key={item.item_name} hover sx={{ '&:hover': { bgcolor: '#fefce8' } }}>
                            <TableCell>
                              <Typography fontWeight="600" sx={{ color: '#1e1b4b' }}>{item.item_name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={item.category || 'Uncategorized'} size="small" sx={{ bgcolor: '#eef2ff', color: '#6366f1' }} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="700" color={item.current_stock <= 0 ? 'error' : 'warning'}>
                                {item.current_stock}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.threshold}</TableCell>
                            <TableCell>{item.unit_of_measurement}</TableCell>
                            <TableCell>
                              <Chip 
                                label={item.status} 
                                size="small" 
                                sx={{ 
                                  bgcolor: item.status === 'Critical' ? '#fef2f2' : '#fffbeb',
                                  color: item.status === 'Critical' ? '#dc2626' : '#d97706',
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<LocalShippingIcon />}
                                sx={{ 
                                  bgcolor: '#6366f1',
                                  borderRadius: 3,
                                  textTransform: 'none',
                                  '&:hover': { bgcolor: '#4f46e5' }
                                }}
                              >
                                Reorder
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Expiring Items & Top Moving Items */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ color: '#ec4899' }} />
                      Expiring Items
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                      Items approaching expiry date
                    </Typography>
                  </Box>
                </Box>
                
                <Grid container spacing={2}>
                  {filteredExpiring.length === 0 ? (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                        <Typography color="text.secondary">No expiring items</Typography>
                      </Box>
                    </Grid>
                  ) : (
                    filteredExpiring.map((item) => (
                      <Grid item xs={12} key={item.id}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #f1f5f9' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="700">{item.item_name}</Typography>
                                <Chip label={item.category} size="small" sx={{ mt: 0.5, bgcolor: '#f1f5f9' }} />
                              </Box>
                              <Chip 
                                label={`${item.days_until_expiry} days left`}
                                size="small"
                                sx={{ 
                                  bgcolor: item.days_until_expiry <= 7 ? '#fef2f2' : '#fffbeb',
                                  color: item.days_until_expiry <= 7 ? '#dc2626' : '#d97706',
                                  fontWeight: 600
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Expires: {item.expiry_date}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              Quantity: {item.present_quantity_available} {item.unit_of_measurement}
                            </Typography>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, (30 - item.days_until_expiry) / 30 * 100)}
                              sx={{ mt: 2, height: 6, borderRadius: 3 }}
                              color={item.days_until_expiry <= 7 ? 'error' : 'warning'}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <TrendingUpIcon sx={{ color: '#10b981' }} />
                  Top Moving Items
                </Typography>
                <Grid container spacing={2}>
                  {dashboardData.stats.top_moving_items?.length === 0 ? (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <TrendingDownIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                        <Typography color="text.secondary">No movement data yet</Typography>
                      </Box>
                    </Grid>
                  ) : (
                    dashboardData.stats.top_moving_items?.map((item, index) => (
                      <Grid item xs={12} sm={6} key={item.item_name}>
                        <Card sx={{ 
                          borderRadius: 3, 
                          textAlign: 'center',
                          background: `linear-gradient(135deg, ${COLORS[index % COLORS.length]}15, white)`,
                          border: `1px solid ${COLORS[index % COLORS.length]}30`
                        }}>
                          <CardContent>
                            <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b' }}>{item.item_name}</Typography>
                            <Typography variant="h3" fontWeight="800" sx={{ color: COLORS[index % COLORS.length], my: 1 }}>
                              {item.total_requested}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Requests: {item.request_count}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 3 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardStoreKeeper;